import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import { type ChecklistDetail, isFiniteNumber } from '../../shared/checklistUtils';
import { formatDealOptionName } from '../../shared/format';
import type { DiscountsMarginRowView } from '../../tabs/discountsMargin/types';
import type { WithChecklistItemMetadata } from '../../types';
import { presentRowBase, warnMissingRuleText } from '../rowBase';

import {
  DISCOUNTS_MARGIN_FIELDS,
  getMarginSpreadRule,
  getMarginSpreadSummary,
  getOfferDiscountsRule,
} from './discountsMarginRuleText';

export function presentDiscountsMarginRows(
  rows: Array<CmChecklist.DiscountsMarginRow>,
): Array<WithChecklistItemMetadata<DiscountsMarginRowView>> {
  return rows.flatMap((row) => {
    const field = DISCOUNTS_MARGIN_FIELDS[row.rule_id];

    if (!field) {
      warnMissingRuleText(row);
      return [];
    }

    const detail =
      row.evidence.check === 'offer_discounts'
        ? presentOfferDiscounts(row.evidence, row.status === 'approved')
        : presentMarginSpread(row.evidence, row.status === 'approved');

    return [{ ...presentRowBase(row), ...detail, field, title: field }];
  });
}

function presentOfferDiscounts(
  evidence: CmChecklist.OfferDiscountsEvidence,
  approved: boolean,
): Pick<DiscountsMarginRowView, 'details' | 'rule' | 'sourceValue' | 'summary'> {
  const details = evidence.discounts.map(({ discount, name, package_id: packageId }) => ({
    discount,
    name: name || packageId || 'Deal option',
  }));

  return {
    details: details.map(({ discount, name }) => ({
      label: name,
      value: isFiniteNumber(discount) ? `${discount}%` : 'Not available',
    })),
    rule: getOfferDiscountsRule(evidence.min_percent, evidence.max_percent),
    sourceValue:
      details.length > 0
        ? details
            .map(({ discount, name }) => `${name}: ${isFiniteNumber(discount) ? `${discount}%` : 'N/A'}`)
            .join('; ')
        : 'No deal options available',
    summary: approved ? 'Offer discounts are within the approved range.' : 'Offer discounts need review.',
  };
}

function presentMarginSpread(
  evidence: CmChecklist.MarginSpreadEvidence,
  approved: boolean,
): Pick<DiscountsMarginRowView, 'details' | 'rule' | 'sourceValue' | 'summary'> {
  const nameOf = (entry: { name: string | null; package_id: string | null }) =>
    formatDealOptionName(entry.name || entry.package_id || 'Deal option');
  const groupEntries: Array<ChecklistDetail> = evidence.conflict_groups.map((group) => ({
    label: `${nameOf(group.anchor)} (${group.anchor.margin}%)`,
    value: `Margin difference exceeds the threshold with ${group.conflicts.length} other deal option${
      group.conflicts.length === 1 ? '' : 's'
    }`,
    expandableItems: group.conflicts.map(
      (conflict) =>
        `${nameOf(conflict)} (${conflict.margin}%) — ${conflict.delta.toFixed(1)}% ${
          conflict.margin > group.anchor.margin ? 'higher' : 'lower'
        } margin`,
    ),
  }));
  const flaggedOptionCount = new Set(
    evidence.conflict_groups.flatMap((group) => [
      group.anchor.package_id,
      ...group.conflicts.map((conflict) => conflict.package_id),
    ]),
  ).size;

  return {
    details: groupEntries.length > 0 ? groupEntries : [{ label: 'Outlier pairs', value: 'None' }],
    rule: getMarginSpreadRule(evidence.threshold_percent),
    sourceValue:
      groupEntries.length > 0
        ? groupEntries.map(({ label, value }) => `${label}: ${value}`).join('; ')
        : 'No outlier pairs',
    summary: getMarginSpreadSummary({ approved, comparableCount: evidence.comparable_count, flaggedOptionCount }),
  };
}
